import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { Store } from '@ngrx/store';

import { AuthService } from '@services';
import * as AuthActions from '../../state/auth/auth.actions';
import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';

interface MenuItem {
  title: string;
  icon: string;
  link: string;
}

@Component({
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    RouterLink,
    RouterLinkActive,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatListModule,
    MatButtonModule,
    BreadcrumbsComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit {
  opened: boolean = true;
  menuItems: MenuItem[] = [
    { title: 'Dashboard', icon: 'dashboard', link: '/dashboard' },
    { title: 'Users', icon: 'people', link: '/users' },
    { title: 'Batch Upload', icon: 'upload', link: '/batch-upload' },
    { title: 'Payments', icon: 'payment', link: '/payments' },
    { title: 'Vehicles', icon: 'moped', link: '/vehicles' },
    { title: 'Map', icon: 'map', link: '/map' },
    { title: 'Incidents', icon: 'report_problem', link: '/incidents' },
    { title: 'Actions', icon: 'touch_app', link: '/actions' },
  ];

  activeItem: MenuItem | undefined;

  constructor(
    private store: Store,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private authService: AuthService
  ) {}

  ngOnInit() {
    if (this.authService.getDecodedToken().role == 'admin') {
      const item = { title: 'Service Providers', icon: 'business', link: '/service-providers' };
      this.menuItems.splice(1, 0, item);

      const customer = { title: 'Customers', icon: 'people', link: '/customers' };
      this.menuItems.splice(2, 0, customer);

      const adminItems = [
        { title: 'Contract', icon: 'article', link: '/contract' },
        {
          title: 'Document Types',
          icon: 'description',
          link: '/document-types',
        },
        {
          title: 'Inspection Images',
          icon: 'image',
          link: '/inspection-image-types',
        },
        { title: 'Expenses', icon: 'request_quote', link: '/expenses' },
        { title: 'Criminal Checks', icon: 'gavel', link: '/criminal-checks' },
      ];

      this.menuItems.splice(3, 0, ...adminItems);

      const serviceRequest = { title: 'Service Requests', icon: 'assignment_add', link: '/service-requests' };
      this.menuItems.splice(9, 0, serviceRequest);

      const trips = { title: 'Trips', icon: 'navigation', link: '/trips' };
      this.menuItems.splice(10, 0, trips);
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.activeItem = this.menuItems.find(
          (item) => item.link === event.url
        );
      }
    });

    this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.Handset])
      .subscribe((result) => {
        result.matches ? (this.opened = false) : (this.opened = true);
      });
  }

  public setActiveItem(item: MenuItem) {
    this.activeItem = item;
  }

  public isActive(link: string): boolean {
    if (link === '/') {
      return this.router.url === link;
    }

    return this.router.url.startsWith(link);
  }

  public trackByLink(index: number, item: MenuItem): string {
    return item.link;
  }

  public onLogoutClicked() {
    this.store.dispatch(AuthActions.logout());
    this.router.navigate(['auth', 'login']);
  }
}
